import React, { useState } from 'react';
import { StarIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function DeuteronomyDroughtMapPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-332">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-333">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-334">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-335">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-336" />
            <span data-id="element-337">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-338"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-339">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-340">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-341">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-342">
              The Drought Deuteronomy Warned Us About and the Map No One Is
              Showing You
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-343">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-344" />
            <div className="text-left space-y-1" data-id="element-345">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-346">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-347">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-348">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-349">
            <p data-id="element-350">
              Most pastors read past Deuteronomy 28 quickly. It's the long
              blessings-and-curses chapter, and the curses are not soft.
            </p>
            <p data-id="element-351">Verse 23 is the one I want you to sit with:</p>

            <blockquote className="border-l-4 border-[#b91c1c] pl-6 py-2 italic text-[#0f172a]" data-id="element-352">
              "And thy heaven that is over thy head shall be brass, and the
              earth that is under thee shall be iron."
            </blockquote>

            <p data-id="element-353">
              Brass sky. Iron ground. A sky so closed-up no rain comes through.
              A ground so hardened no water sinks in even when it does.{' '}
              <b data-id="element-354">Drought from above and drought from below at the same time.</b>
            </p>
            <p data-id="element-355">
              I'm not telling you that verse is being literally fulfilled in
              America today. I'm a man, not a prophet. What I am saying is the
              framework Moses laid down — that a nation's relationship to its
              water reflects its relationship to its God — is older than any
              state legislature, and we should not be surprised when it shows up
              in the data.
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-356">
              The map almost no one shows you
            </h2>

            <p data-id="element-357">
              Every Thursday morning the U.S. Department of Agriculture and NOAA
              jointly publish the <b data-id="element-358">U.S. Drought Monitor</b>. It's free,
              public, and almost no major news network mentions it unless
              there's a wildfire that needs B-roll.
            </p>
            <p data-id="element-359">
              The map uses five categories: D0 abnormally dry, D1 moderate, D2
              severe, D3 extreme, D4 exceptional.
            </p>
            <p data-id="element-360">
              Pull it up for any week of the past five years and you'll see the
              same pattern. Large stretches of the western United States —
              California, Nevada, Arizona, New Mexico, parts of Texas, Oklahoma,
              Kansas, Colorado, Utah — sitting persistently in <b data-id="element-361">D2 to D4</b>.
              Not for a season. <b data-id="element-362">For years.</b>
            </p>
            <p data-id="element-363">
              The Colorado River basin, which supplies water to about forty
              million Americans across seven states, has been in declared
              shortage status. Lake Mead and Lake Powell have spent extended
              stretches at the lowest levels recorded since they were filled.
            </p>
            <p data-id="element-364">That's not a forecast. That's the present.</p>
            <p data-id="element-365">
              The honest read of Deuteronomy 28 and the U.S. Drought Monitor,
              taken together, is this:{' '}
              <b data-id="element-366">
                the relationship between this country and its water is no longer
                guaranteed.
              </b>{' '}
              The household that wants to be ahead of that — not in panic, in
              faith — has to take its own water situation off autopilot.
            </p>

            <blockquote className="border-l-4 border-[#b91c1c] pl-6 py-2 italic text-[#0f172a]" data-id="element-367">
              Noah didn't wait for the rain. Joseph didn't wait for the famine.
              The brass sky is not waiting either.
            </blockquote>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-368">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-369">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-370">
            {/* Card 1 */}
            <div className="group cursor-pointer space-y-3" data-id="element-371">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-372">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-373" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-374">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-375">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            {/* Card 2 */}
            <div className="group cursor-pointer space-y-3" data-id="element-376">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-377">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-378" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-379">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-380">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            {/* Card 3 */}
            <div className="group cursor-pointer space-y-3" data-id="element-381">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-382">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-383" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-384">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-385">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-386" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-387">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-388">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-389">
            <div className="relative w-10 h-10" data-id="element-390">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-391" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-392" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-393">
            <p data-id="element-394">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-395">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-396">
                Terms & Conditions
              </button>
              <span data-id="element-397">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-398">
                Privacy Policy
              </button>
              <span data-id="element-399">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-400">
                Contact Us
              </button>
            </p>
            <p data-id="element-401">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-402">
          <div className="absolute inset-0 bg-black/50" data-id="element-403" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-404">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-405">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-406">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-407">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-408" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-409">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-410">
                    Last Updated: May 2026
                  </p>
                  <p data-id="element-411">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                    Please read them carefully before using our services.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-412">
                    1. Acceptance of Terms
                  </h3>
                  <p data-id="element-413">
                    By accessing this website, you acknowledge that you have
                    read, understood, and agree to be bound by these Terms &
                    Conditions. If you do not agree, please do not use this
                    website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-414">
                    2. Use of Content
                  </h3>
                  <p data-id="element-415">
                    All content on this website, including text, graphics,
                    images, and other material, is for informational purposes
                    only. No part of this report may be reproduced or placed on
                    any electronic medium without written permission from the
                    publisher.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-416">
                    3. Disclaimer
                  </h3>
                  <p data-id="element-417">
                    Information contained herein is obtained from sources
                    believed to be reliable, but its accuracy cannot be
                    guaranteed. The content is not intended as professional
                    advice. Always consult qualified professionals for specific
                    guidance.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-418">
                    4. Limitation of Liability
                  </h3>
                  <p data-id="element-419">
                    PatriotsInformer Media, LLC shall not be liable for any
                    damages arising from the use or inability to use the
                    materials on this website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-420">
                    5. Governing Law
                  </h3>
                  <p data-id="element-421">
                    These terms shall be governed by and construed in accordance
                    with the laws of the State of Texas, without regard to its
                    conflict of law provisions.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-422">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-423">
                    Last Updated: May 2026
                  </p>
                  <p data-id="element-424">
                    PatriotsInformer Media, LLC ("we," "us," or "our") is
                    committed to protecting your privacy. This Privacy Policy
                    explains how we collect, use, and safeguard your
                    information.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-425">
                    1. Information We Collect
                  </h3>
                  <p data-id="element-426">
                    We may collect personal information such as your name and
                    email address when you voluntarily submit it through our
                    opt-in forms. We also collect non-personal information such
                    as browser type, IP address, and pages visited.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-427">
                    2. How We Use Your Information
                  </h3>
                  <p data-id="element-428">
                    We use the information we collect to send you the content
                    you requested, communicate updates and offers, improve our
                    website and services, and comply with legal obligations.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-429">
                    3. Email Communications
                  </h3>
                  <p data-id="element-430">
                    By providing your email address, you consent to receive
                    emails from us. You can unsubscribe at any time by clicking
                    the "unsubscribe" link at the bottom of any email.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-431">
                    4. Third-Party Sharing
                  </h3>
                  <p data-id="element-432">
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share information with trusted service
                    providers who assist us in operating our website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-433">
                    5. Data Security
                  </h3>
                  <p data-id="element-434">
                    We implement reasonable security measures to protect your
                    personal information. However, no method of transmission
                    over the Internet is 100% secure.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-435">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'contact' && <>
                  <p data-id="element-436">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-437">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-438">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-439">
                      10160 Highway 242 Ste 800
                      <br data-id="element-440" />
                      PMB 1089
                      <br data-id="element-441" />
                      Conroe, TX 77385
                    </p>
                    <p data-id="element-442">
                      <span className="font-semibold text-[#0f172a]" data-id="element-443">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-444">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-445">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}